import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
  emailBuilderLoadPreview,
  emailBuilderSetMode,
} from "redux/actions/emailBuilder/emailBuilder";
import { useEditorStore } from "../../domain/redux/hooks";
import { selectDocument } from "../../domain/selectors";
import { EmailBuilderPreviewLoadingFrame } from "./EmailBuilderPreviewLoadingFrame";
import { EmailBuilderPreviewFrame } from "./EmailBuilderPreviewFrame";
import "./emailBuilderPreview.scss";

export function EmailBuilderPreview() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const workspace = useAppSelector(store => store.emailBuilderStore.workspace);
  const campaign = useAppSelector(store => store.emailBuilderStore.campaign);
  const previewStatus = useAppSelector(store => store.emailBuilderStore.previewStatus);
  const previewHtml = useAppSelector(store => store.emailBuilderStore.previewHtml);
  const previewError = useAppSelector(store => store.emailBuilderStore.previewError);
  const previewViewport = useAppSelector(store => store.emailBuilderStore.previewViewport);
  const emailDocument = useEditorStore(selectDocument);
  const pageBackgroundColor = emailDocument.settings.pageBackgroundColor;
  const contentWidthPx = emailDocument.settings.contentWidth;

  const workspaceId = workspace?.id ?? campaign?.id ?? null;
  const workspaceKind = workspace?.kind ?? "campaign";

  useEffect(() => {
    if (workspaceId === null) {
      return;
    }

    void dispatch(emailBuilderLoadPreview({ kind: workspaceKind, id: workspaceId }));
  }, [workspaceId, workspaceKind, dispatch]);

  function renderBody() {
    if (previewStatus === "idle" || previewStatus === "loading") {
      return (
        <EmailBuilderPreviewLoadingFrame
          previewViewport={previewViewport}
          contentWidthPx={contentWidthPx}
        />
      );
    }

    if (previewStatus === "failed") {
      return (
        <div className="email-builder-preview-status email-builder-preview-status--error">
          {previewError !== null ? t(previewError) : t("Could not load email preview.")}
        </div>
      );
    }

    if (previewHtml === null) {
      return (
        <div className="email-builder-preview-status email-builder-preview-status--error">
          {t("Could not load email preview.")}
        </div>
      );
    }

    return (
      <EmailBuilderPreviewFrame
        previewHtml={previewHtml}
        previewViewport={previewViewport}
        contentWidthPx={contentWidthPx}
        iframeTitle={t("Email preview")}
        onEscape={() => dispatch(emailBuilderSetMode("design"))}
      />
    );
  }

  const previewShellStyle =
    pageBackgroundColor === null ? undefined : { backgroundColor: pageBackgroundColor };

  return (
    <div
      className={`email-builder-preview email-builder-preview--${previewViewport}`}
      style={previewShellStyle}
    >
      <div className="email-builder-preview-body">{renderBody()}</div>
    </div>
  );
}
